//* Libraries imports
import React, { useState } from "react";
import { EyeIcon } from "lucide-react";

//* Components imports
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CircularProgress } from "@mui/material";

export type MediaUploadProps = {
  type: "audio" | "video" | "pdf";
  data: { link: string; name: string };
  onUpdate: (field: "link" | "name", value: string) => void;
  onUpload: (file: File) => Promise<{ data: { path: string } }>;
  loadingFile?: boolean;
  id?: string;
  style?:any;
  hidden_name?:boolean;
};

export function MediaUpload({
  type,
  data,
  onUpdate,
  onUpload,
  loadingFile = false,
  id = "tutorial-upload",
  style={},
  hidden_name=false
}: MediaUploadProps) {
  const [loadingLocal, setLoadingLocal] = useState(false);
  const fileInputId = `file_upload_${type}_${id}`;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;

    const file = e.target.files[0];

    try {
      setLoadingLocal(true);
      const response = await onUpload(file);
      onUpdate("link", response.data.path);
    } catch (error) {
      console.error("Error uploading file:", error);
    } finally {
      setLoadingLocal(false);
      e.target.value = "";
    }
  };

  const getPreview = () => {
    const url =
      (data.link as any) instanceof File
        ? URL.createObjectURL(data.link as unknown as File)
        : data.link;

    if (type === "audio") {
      return <audio controls style={{ width: "100%" }} src={url} />;
    }
    if (type === "video") {
      return (
        <video
          height={100}
          controls
          style={{ width: "100%", height: 200 }}
          src={url}
        />
      );
    }
    if (type === "pdf") {
      return (
        <iframe
          title={`tutorial_${type}`}
          height={100}
          style={{ width: "100%", height: 200 }}
          src={url}
        />
      );
    }
    return null;
  };

  const acceptType =
    type === "video"
      ? "video/*"
      : type === "audio"
        ? "audio/*"
        : "application/pdf";

  return (
    <div className={data.link ? "grid grid-cols-2" : ""}  style={{ width:hidden_name?'100%':'' }}>
      {(loadingLocal || loadingFile) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/50">
          <CircularProgress size={24} />
        </div>
      )}

      {hidden_name==true && data.link && (
        <div className="relative gap-2.5 px-[19px] py-5 bg-clarofrente-01 rounded-xl border-[0.5px] border-[#d8e0f0] mr-2">
          <Button
            variant="outline"
            size="icon"
            className="absolute top-4 right-2 h-8 w-8 bg-white/90 backdrop-blur-sm border-gray-200 rounded-full shadow-sm hover:bg-white hover:text-blue-500 z-20"
            onClick={() => {
              const url =
                (data.link as any) instanceof File
                  ? URL.createObjectURL(data.link as unknown as File)
                  : data.link;
              window.open(url, "_blank");
            }}
          >
            <EyeIcon className="size-4" />
          </Button>
          <div>
            {getPreview()}
            <label className="text-sm font-semibold text-gray-700 mt-1 block">
              Nome
            </label>
            <Input
              className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
              placeholder="Explicação do produto"
              value={data.name}
              onChange={(e) => onUpdate("name", e.target.value)}
            />
          </div>
        </div>
      )}

      <div
        style={{ cursor: "pointer",width:hidden_name?'100%':'' }}
        onClick={() => document.getElementById(fileInputId)?.click()}
        className={"ml-2 flex-1 flex flex-col items-center justify-center gap-2.5 px-[19px] py-5 bg-clarofrente-01 rounded-xl border-[0.5px] border-[#d8e0f0] min-h-[100px] "}
      >
        <img src="/cloudupload.png" width={50} alt="Upload" />
        <span className="text-sm text-gray-500 font-medium">
          {type === "pdf"
            ? "Upload PDF"
            : type === "video"
              ? "Upload Vídeo"
              : "Upload Áudio"}
        </span>
      </div>

      <input
        name="file"
        aria-label="file"
        type="file"
        id={fileInputId}
        className="hidden"
        disabled={loadingFile || loadingLocal}
        accept={acceptType}
        onChange={handleFileUpload}
      />
    </div>
  );
}
